import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { ShieldCheck, LayoutDashboard, Building2, UserPlus, LogOut, ScrollText, KeyRound } from "lucide-react";

const NAV = [
  { to: "/platform-admin",               end: true, label: "Dashboard",     icon: LayoutDashboard, testid: "nav-platform-dashboard" },
  { to: "/platform-admin/organisations",           label: "Organisations", icon: Building2,        testid: "nav-platform-orgs" },
  { to: "/platform-admin/onboarding",              label: "Onboarding",    icon: UserPlus,         testid: "nav-platform-onboarding" },
  { to: "/platform-admin/audit-log",                label: "Audit Log",    icon: ScrollText,       testid: "nav-platform-audit" },
  { to: "/platform-admin/security",                 label: "My Security", icon: KeyRound,         testid: "nav-platform-security" },
];

export default function PlatformShell() {
  const { token, admin, ready, logout } = usePlatformAuth();
  const navigate = useNavigate();
  if (!ready) return <div className="p-6 text-slate-500 text-sm">Loading platform admin…</div>;
  if (!token || !admin) return <Navigate to="/platform-admin/login" replace />;

  return (
    <div className="h-screen w-screen flex bg-slate-950 text-slate-100" data-testid="platform-shell">
      <aside className="w-60 shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="h-14 px-4 flex items-center gap-2 border-b border-slate-800">
          <div className="w-8 h-8 rounded-md bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">Ayobell</div>
            <div className="text-sm font-heading font-semibold">Platform Admin</div>
          </div>
        </div>
        <nav className="flex-1 py-3 space-y-1 px-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm ${isActive ? "bg-emerald-500/20 text-emerald-300" : "text-slate-300 hover:bg-slate-800"}`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-[11px] text-slate-500 truncate" data-testid="platform-me-email">{admin.email}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {admin.mfa_enabled ? (
              <span className="text-emerald-400" data-testid="platform-mfa-badge">MFA enabled</span>
            ) : (
              <span className="text-amber-400" data-testid="platform-mfa-warning">MFA not enabled</span>
            )}
          </div>
          <button
            onClick={async () => { await logout(); navigate("/platform-admin/login"); }}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-semibold text-slate-200 border border-slate-700 hover:bg-slate-800"
            data-testid="platform-logout"
          ><LogOut className="w-3.5 h-3.5" /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-auto bg-slate-950">
        <Outlet />
      </main>
    </div>
  );
}
